import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'react-feather';
import { useTranslation } from 'react-i18next';
import { Button } from '@components-library/Button';
import { keyMap } from '@locales/lokalise/keyMap';
import cn from 'classnames';
import styles from './styles.module.scss';
//@ts-ignore
import { useCollapse } from './useCollapseHook/useCollapse';

/**
 * Temporary Solution Notice
 * @description We have identified an issue where the 'react-collapsed' library is using mjs files,
 * which are unfortunately not supported in some browsers, including the UC Browser.
 * To overcome this issue, we have taken the required file, modified it and stored it locally.
 * Please note that this is a temporary workaround until the library provides a fix or a version that doesn't rely on mjs files,
 * or the browser vendors update their software to support mjs files.
 */

const TextCollapse = ({
  className,
  collapsedHeight = 120,
  children,
  mask = true,
}: {
  className?: string;
  collapsedHeight?: number;
  children: React.ReactNode;
  mask?: boolean;
}) => {
  const [isExpanded, setExpanded] = useState(false);
  const [isMoreButtonDisplayed, setIsMoreButtonDisplayed] = useState(false);
  const { getCollapseProps, getToggleProps } = useCollapse({ collapsedHeight });
  const { i18n } = useTranslation();

  return (
    <>
      <section
        {...getCollapseProps()}
        className={cn(className, {
          [styles.mask]: !isExpanded && mask,
        })}>
        {children}
      </section>

      {isMoreButtonDisplayed && (
        <div className={styles.maskWrapper}>
          <div className={styles.mask} />
          <Button
            className={styles.button}
            {...getToggleProps({
              onClick: () => {
                setExpanded((prevExpanded) => !prevExpanded);
              },
            })}>
            <span className={styles.buttonInner}>
              {isExpanded ? i18n.t(keyMap.sharedActionsLess) : i18n.t(keyMap.sharedActionsMore)}
              {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </span>
          </Button>
        </div>
      )}

      <div
        ref={(ref) => {
          const clientHeight = ref?.clientHeight ?? 0;

          if (clientHeight > collapsedHeight) {
            setIsMoreButtonDisplayed(true);
          }
        }}
        className={cn(styles.hiddenBlock, className)}>
        {children}
      </div>
    </>
  );
};

export { TextCollapse };
